import React, { useState, useEffect, useMemo } from 'react';
import { useQueryWithSimpleErrorHandling, useDictionaryHandling, useSimpleError, useInsuranceTypeConfiguration } from 'hooks';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import { INSURERS_QUERY } from 'features/insuranceDocument/edit/general/insuranceData/queries';
import { INSURANCE_TYPES_QUERY } from '../../../../insuranceDocument/queries/insuranceDocumentQueries';
import { INSURANCE_SERVICE_DETAILS } from '../../../queries';
import { CHECK_SERVICE } from '../../../mutations';
import {
  SERVICE_CONNECTION_CHECKED,
  DB_CONNECTION_CHECKED,
  INSURERS_CONNECTION_CHECKED,
  SERVICE_STATUS_CHECKED
} from '../../../subscriptions';
import { curry } from 'lodash';
import { insuranceTypeNames } from 'constants/defaultValues';
import InsuranceFlowChecksHeader from '../InsuranceFlowChecksHeader';
import InsuranceFlowRcaChecksComponent from './InsuranceFlowRcaChecksComponent';
import RcaCrossCheckFlowContainer from './RcaCrossCheckFlowContainer';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

const InsuranceFlowRcaChecksContainer = () => {
  const showError = useSimpleError();
  const { t } = useTranslation();
  const [selectedChecks, setSelectedChecks] = useState({});
  const [startCheckDisabled, setStartCheckDisabled] = useState(true);
  const [selectDisabled, setSelectDisabled] = useState(false);
  const [serviceConnectionCheck, setServiceConnectionCheck] = useState({ loading: false });
  const [dbConnectionCheck, setDbConnectionCheck] = useState({ loading: false });
  const [insurersConnectionCheck, setInsurersConnectionCheck] = useState([]);
  const [insurers, setInsurers] = useState([]);

  const { rcaInsuranceTypeConfiguration } = useInsuranceTypeConfiguration(new Array(insuranceTypeNames.RCA));

  const { getIdByCode: getInsuranceTypeIdByCode } = useDictionaryHandling(INSURANCE_TYPES_QUERY, 'insuranceTypes');

  const insuranceTypeCode = rcaInsuranceTypeConfiguration?.PropertyCode;

  const insuranceTypeId = useMemo(() => getInsuranceTypeIdByCode(insuranceTypeCode), [getInsuranceTypeIdByCode, insuranceTypeCode]);

  const { data: insuranceServiceData } = useQueryWithSimpleErrorHandling(INSURANCE_SERVICE_DETAILS);

  useQueryWithSimpleErrorHandling(INSURERS_QUERY, {
    variables: {
      insuranceTypeId: insuranceTypeId
    },
    skip: !insuranceTypeId,
    onCompleted: data => {
      setInsurers(data?.insurers);
    },
    notifyOnNetworkStatusChange: true
  });

  const handleServerError = () => {
    setStartCheckDisabled(false);
    setSelectDisabled(false);
    resetChecks();
  };

  const [startCheck] = useMutation(CHECK_SERVICE, {
    onError: error => {
      showError(error);
      handleServerError();
    }
  });

  useConditionalSubscription(SERVICE_CONNECTION_CHECKED, {
    onData: ({ data: { data } }) => {
      setServiceConnectionCheck({
        ...serviceConnectionCheck,
        success: data.serviceConnectionChecked.success,
        errorMessage: data.serviceConnectionChecked.errorMessage,
        loading: false
      });
    }
  });

  useConditionalSubscription(DB_CONNECTION_CHECKED, {
    onData: ({ data: { data } }) => {
      setDbConnectionCheck({
        ...dbConnectionCheck,
        success: data.dbConnectionChecked.success,
        errorMessage: data.dbConnectionChecked.errorMessage,
        loading: false
      });
    }
  });

  useEffect(() => {
    if (insurers) {
      setInsurersConnectionCheck(insurers.map(insurer => Object.assign({}, insurer, { loading: false })));
    }
  }, [insurers]);

  useEffect(() => {
    if (
      (selectedChecks && Object.values(selectedChecks).every(item => item === false)) ||
      serviceConnectionCheck.loading ||
      dbConnectionCheck.loading ||
      insurersConnectionCheck.some(insurer => insurer.loading === true)
    ) {
      setStartCheckDisabled(true);
      return;
    }
    setStartCheckDisabled(false);
  }, [insurersConnectionCheck, dbConnectionCheck, serviceConnectionCheck, selectedChecks]);

  useConditionalSubscription(INSURERS_CONNECTION_CHECKED, {
    onData: ({ data: { data } }) => {
      setInsurersConnectionCheck(
        insurersConnectionCheck.map(insurer => {
          return insurer.id !== data.insurersConnectionChecked.id
            ? insurer
            : Object.assign({}, insurer, {
                success: data.insurersConnectionChecked.success,
                loading: false
              });
        })
      );
    }
  });

  useConditionalSubscription(SERVICE_STATUS_CHECKED, {
    onData: ({ data: { data } }) => {
      setSelectDisabled(!data.serviceStatusChecked.success);
    }
  });

  const handleCheckSelect = curry((prop, value) => {
    setSelectedChecks({
      ...selectedChecks,
      [prop]: value
    });
  });

  const resetChecks = useCallback(() => {
    setServiceConnectionCheck({ loading: false });
    setDbConnectionCheck({ loading: false });
    setInsurersConnectionCheck(
      insurersConnectionCheck.map(insurerConnection =>
        Object.assign(
          {},
          {
            name: insurerConnection.name,
            id: insurerConnection.id,
            externalId: insurerConnection.externalId,
            loading: false
          }
        )
      )
    );
  }, [insurersConnectionCheck]);

  const handleChecksStart = useCallback(() => {
    resetChecks();
    setStartCheckDisabled(true);
    setSelectDisabled(true);
    selectedChecks.checkServiceConnection && setServiceConnectionCheck({ ...serviceConnectionCheck, loading: true });
    selectedChecks.checkDbConnection && setDbConnectionCheck({ ...dbConnectionCheck, loading: true });
    selectedChecks.checkInsurersConnection &&
      setInsurersConnectionCheck(insurersConnectionCheck.map(insurerConnection => ({ ...insurerConnection, loading: true })));
    startCheck({
      variables: {
        input: {
          ...selectedChecks,
          insurers: selectedChecks.checkInsurersConnection ? insurers.map(insurer => insurer.id) : []
        }
      }
    });
  }, [dbConnectionCheck, insurers, insurersConnectionCheck, resetChecks, selectedChecks, serviceConnectionCheck, startCheck]);

  return (
    <>
      <InsuranceFlowChecksHeader title={t('Settings.InsuranceIntegrationChecks.RcaCheck.Title')} />
      <InsuranceFlowRcaChecksComponent
        selectedChecks={selectedChecks}
        onCheckSelect={handleCheckSelect}
        onChecksStart={handleChecksStart}
        startCheckDisabled={startCheckDisabled}
        selectDisabled={selectDisabled}
        serviceConnectionCheck={serviceConnectionCheck}
        dbConnectionCheck={dbConnectionCheck}
        insurersConnectionCheck={insurersConnectionCheck}
        insuranceServiceTestModeCheck={insuranceServiceData?.insuranceServiceDetails}
      />
      <RcaCrossCheckFlowContainer />
    </>
  );
};

export default InsuranceFlowRcaChecksContainer;
